import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { assertFirebaseConfigured, db } from '../lib/firebase/config';

export const STAFF_PROFILE_CATEGORIES = [
  'Education Team',
  'Content Writer',
  'Support Staff',
];

function categorySortRank(category) {
  const index = STAFF_PROFILE_CATEGORIES.indexOf(category);
  return index === -1 ? STAFF_PROFILE_CATEGORIES.length : index;
}

function toDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  return null;
}

function sanitizeAuthorCreator(payload, options = {}) {
  const source = payload || {};
  const { requireName = false } = options;
  const hasField = (fieldName) =>
    Object.prototype.hasOwnProperty.call(source, fieldName);
  const safePayload = {};

  if (hasField('name') || requireName) {
    const name = (source.name ?? '').toString().trim();
    if (!name) {
      throw new Error('Staff profile name is required.');
    }
    safePayload.name = name;
  }

  if (hasField('category') || requireName) {
    const category = (source.category ?? STAFF_PROFILE_CATEGORIES[0]).toString().trim();
    if (!STAFF_PROFILE_CATEGORIES.includes(category)) {
      throw new Error('Invalid staff profile category.');
    }
    safePayload.category = category;
  }

  const optionalStringFields = [
    'profile',
    'website',
    'projectTitleRole',
    'socialMedia',
    'headshotUrl',
    'headshotPath',
    'bio',
    'threeFunFacts',
  ];

  for (const fieldName of optionalStringFields) {
    if (hasField(fieldName)) {
      safePayload[fieldName] = (source[fieldName] ?? '').toString().trim();
    }
  }

  return safePayload;
}

function normalizeAuthorCreator(snapshot) {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export async function listAuthorCreators() {
  assertFirebaseConfigured();
  const snapshot = await getDocs(collection(db, 'authorCreators'));

  return snapshot.docs
    .map(normalizeAuthorCreator)
    .sort((a, b) => {
      const categoryDifference =
        categorySortRank(a.category) - categorySortRank(b.category);
      if (categoryDifference !== 0) {
        return categoryDifference;
      }

      return (a.name || '').localeCompare(b.name || '', undefined, {
        sensitivity: 'base',
      });
    });
}

export async function createAuthorCreator(payload, user) {
  assertFirebaseConfigured();
  const safePayload = sanitizeAuthorCreator(payload, { requireName: true });

  const ref = await addDoc(collection(db, 'authorCreators'), {
    ...safePayload,
    createdBy: user?.uid ?? null,
    updatedBy: user?.uid ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return ref.id;
}

export async function updateAuthorCreator(id, payload, user) {
  assertFirebaseConfigured();
  const safePayload = sanitizeAuthorCreator(payload, { requireName: false });
  const ref = doc(db, 'authorCreators', id);

  await updateDoc(ref, {
    ...safePayload,
    updatedBy: user?.uid ?? null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteAuthorCreator(id) {
  assertFirebaseConfigured();
  const ref = doc(db, 'authorCreators', id);
  await deleteDoc(ref);
}
